import { showNotification } from '@mantine/notifications';
import axios from 'axios';
import { Buffer } from 'buffer';
import { useEffect, useRef, useState } from 'react';
import { useApp } from '../contexts/app.context';
import { FILE_UPLOADED, GENERATE_SIGN_UPLOAD_FILE, GET_FILE, UPLOAD_CANVA_FILE, UPLOAD_DRIVE_FILE } from '../services/files';

export const useFileUploader = ({ file, onFileUploaded, deletable = true }) => {
    const [error, setError] = useState<string>(null);
    const [fileSize, setFileSize] = useState<number>(0);
    const [serverProgress, setServerProgress] = useState(0);
    const [awsProgress, setAwsProgress] = useState(0);
    const [progress, setProgress] = useState(0);
    const [fileId, setFileId] = useState(null);
    const [filename, setFilename] = useState("Novo arquivo");
    const [fileType, setFileType] = useState("image");

    const { socket } = useApp();

    const widthRef = useRef<number>();
    const heightRef = useRef<number>();
    const sizeRef = useRef<number>();
    const durationRef = useRef<number>();
    const deletableRef = useRef<boolean>(deletable);

    const getOthers = () => {
        return {
            width: widthRef.current,
            height: heightRef.current,
            size: sizeRef.current,
            duration: durationRef.current,
        }
    }

    const getFileType = (type) => {
        type = type ?? "";
        let fieldType = "document";
        if (type.includes("image")) fieldType = "image";
        if (type.includes("video")) fieldType = "video";
        if (type.includes("audio")) fieldType = "audio";
        return fieldType;
    }

    const signUpload = (file, params: {
        size,
        filename,
        contentType,
        mimetype,
    }) => {
        GENERATE_SIGN_UPLOAD_FILE({ ...params, d: deletableRef.current })
            .then(res => {
                axios.post(res.signedUrl, file, {
                    // headers: { 'Content-Type': file.type },
                    headers: {
                        "X-Bz-File-Name": res.fileKey,
                        "Content-Type": file.type || "b2/x-auto",
                        "X-Bz-Content-Sha1": "do_not_verify",
                        "Authorization": res.authorizationToken,
                    },
                    responseType: "json",
                    onUploadProgress: (progressEvent) => {
                        const totalLength = progressEvent.lengthComputable ? progressEvent.total : progressEvent.target.getResponseHeader('content-length') || progressEvent.target.getResponseHeader('x-decompressed-content-length');
                        if (totalLength !== null) {
                            setServerProgress(Math.round((progressEvent.loaded * 100) / totalLength));
                            setAwsProgress(Math.round((progressEvent.loaded * 100) / totalLength));
                        }
                    },
                })
                    .then(({ data: uploadRes }) => {
                        console.log({ uploadRes })
                        onFileUploaded({ url: res.generatedUrl, type: getFileType(params.contentType), ...getOthers() })
                        FILE_UPLOADED(res._id, { url: res.generatedUrl, fileId: uploadRes.fileId })
                            .then(() => { })
                            .catch(err => {
                                showNotification({ message: err.message, color: "red" })
                            })
                    })
                    .catch(err => {
                        setError(err.message);
                        showNotification({ message: err.message, color: "red" })
                    })
            })
            .catch(err => {
                setError(err.message);
                showNotification({ title: "Falha ao salvar arquivo", message: err.message, color: 'red' })
            })
    }

    const handleInsertFile = async (file) => {
        try {
            setFilename(file.name);
            console.log(file);
            let fieldType = getFileType(file.type);
            setFileType(fieldType);
            setFileSize(file.size);
            if (file.size > 600000000) throw new Error("Arquivo muito grande, máximo 600MB");

            if (fieldType === "image") {
                await new Promise((resolve, reject) => {
                    var fr = new FileReader();
                    fr.onload = function () {
                        var img: any = new Image();
                        img.onload = function () {
                            widthRef.current = img.width;
                            heightRef.current = img.height;
                            sizeRef.current = file.size;
                            if (img.width > 8500 || img.height > 8500) reject(new Error(`Imagem com resoluções muito grandes - ${img.width} x ${img.height}px`));
                            else resolve(true);
                        };
                        img.src = fr.result;
                    };
                    fr.readAsDataURL(file);
                })
            }

            if (fieldType === "video") {
                await new Promise((resolve, reject) => {
                    const video = document.createElement("video");
                    video.preload = "metadata";

                    video.onloadedmetadata = () => {
                        window.URL.revokeObjectURL(video.src);
                        const width = video.videoWidth;
                        const height = video.videoHeight;
                        const duration = video.duration;

                        widthRef.current = width;
                        heightRef.current = height;
                        sizeRef.current = file.size;
                        durationRef.current = duration;

                        // alert(JSON.stringify({
                        //     widthRef: widthRef.current,
                        //     heightRef: heightRef.current,
                        //     sizeRef: sizeRef.current,
                        //     durationRef: durationRef.current,
                        // }))
                        // if (width > 1920 || height > 1080) {
                        //     reject(new Error(`Vídeo com resolução muito grande - ${width} x ${height}px`));
                        // } else if (duration > 90) {
                        //     reject(new Error(`Vídeo muito longo (${Math.ceil(duration)} segundos), máximo permitido: 90s`));
                        // } else {
                        resolve(true);
                        // }
                    };

                    video.onerror = () => {
                        reject(new Error("Erro ao carregar metadados do vídeo."));
                    };

                    video.src = URL.createObjectURL(file);
                });
            }

            signUpload(file, {
                size: file.size,
                filename: file.name,
                contentType: file.type,
                mimetype: file.type,
            })
        } catch (err) {
            setError(err.message);
            showNotification({ message: err.message, color: "red" })
        }
    }

    const handleInsertDriveV2 = async () => {
        console.log("handleInsertDrive", file);

        try {
            const { id, mimeType, name, sizeBytes: size, url, embedUrl } = file.data;
            setFileSize(size);
            setFilename(name);
            setFileType(getFileType(mimeType));

            // if (size > 512000000) throw new Error("Arquivo muito grande, máximo 512MB");
            if (size > 600000000) throw new Error("Arquivo muito grande, máximo 600MB");

            const downloadUrl = `https://www.googleapis.com/drive/v3/files/${id}?alt=media`;

            const { data: driveRes } = await axios({
                url: downloadUrl,
                method: 'GET',
                responseType: 'arraybuffer',
                headers: {
                    Authorization: `Bearer ${file.auth.oauthAccessToken}`,
                }
            });

            const buffer = Buffer.from(driveRes, "binary");
            // setServerProgress(100);
            signUpload(buffer, { size, filename: name, contentType: mimeType, mimetype: mimeType });

        } catch (err) {
            setError(err.message);
            showNotification({ title: "Falha ao importar arquivo", message: err.message, color: 'red' });
        }
    }

    const handleInsertDrive = async () => {
        console.log("handleInsertDrive", file);

        UPLOAD_DRIVE_FILE(
            { fileId: file?.data?.id, oauthId: file?.auth?.oauthId },
        )
            .then(res => {
                setServerProgress(100);
                setFileSize(res.size);
                setFilename(res.original_name);
                setFileId(res._id);
                setFileType(getFileType(res.type));
            })
            .catch(err => {
                setError(err.message)
                showNotification({ title: "Falha ao importar arquivo", message: err.message, color: 'red' })
            })
    }

    const handleInsertCanva = async () => {
        console.log("handleInsertCanva", file);

        UPLOAD_CANVA_FILE(
            { ...file?.data, oauthId: file?.auth?.oauthId },
        )
            .then(res => {
                setServerProgress(100);
                setFileSize(res.size);
                setFilename(res.original_name);
                setFileId(res._id);
                setFileType(getFileType(res.type));
            })
            .catch(err => {
                setError(err.message)
                showNotification({ title: "Falha ao importar arquivo", message: err.message, color: 'red' })
            })
    }

    // useEffect(() => { setProgress((serverProgress * 0.4) + (awsProgress * 0.6)) }, [serverProgress, awsProgress]);
    useEffect(() => { setProgress(awsProgress) }, [serverProgress, awsProgress]);


    useEffect(() => {
        if (fileId && ["drive", "canva"].includes(file.origin) && socket && (awsProgress ?? 0) < 100) {
            const func = (params) => {
                const { progress, url, error } = params;
                if (error) setError(error);
                if (url) {
                    onFileUploaded({ url, type: fileType, ...getOthers() });
                    setTimeout(() => { setAwsProgress(100) }, 800);
                } else {
                    if (progress) {
                        setTimeout(() => {
                            setAwsProgress(progress);
                        }, 800)
                    }
                }
            }
            const refreshInterval = setInterval(() => {
                GET_FILE(fileId)
                    .then(({ url }) => {
                        if (url) {
                            onFileUploaded({ url, type: fileType, ...getOthers() });
                            setTimeout(() => { setAwsProgress(100) }, 800);
                        }
                    })
                    .catch(ignored => { })
            }, 15000);
            const evName = `file_upload_${fileId}`;
            socket.on(evName, func)
            return () => {
                socket.off(evName, func);
                clearInterval(refreshInterval)
            }
        }
    }, [file?.origin, fileId, socket, fileType, awsProgress]);

    useEffect(() => {
        deletableRef.current = deletable;
    }, [deletable]);
    
    useEffect(() => {
        if (file.origin) {
            if (file.origin === "drive") handleInsertDriveV2();
            if (file.origin === "canva") handleInsertCanva();
        } else {
            handleInsertFile(file);
        }
    }, [file]);

    return {
        name: filename,
        type: fileType,
        progress,
        error,
        size: fileSize,
    };
}